import { Checkbox, Icon } from '@material-ui/core';
import { useEffect, useRef, useState } from 'react';
import { Field, useField } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';
import styled, { css } from 'styled-components';
import { NotesIcon } from '../../icons/notes';
import { AddCheckboxIcon } from '../../../CreateVehicle/icons/AddCheckboxIcon';
import { DoubleCaretDown, DoubleCaretUp } from '../../../../components/Icons/DoubleCaret';
import { EditingCheckboxField } from './EditingCheckboxField';
import { useUpdateCheckboxMutation } from 'services/api/vehicle-sections';
import { NotesStatusIcon } from './NotesStatusIcon';
import { useUpdateCheckbox } from '../../hooks/useUpdateCheckbox';
import { useFinalReviewDialogOpened } from 'services/listerUploadVehicle/v2';
import { CheckboxNotes } from './CheckboxNotes';
import GptIcon from 'components/Icons/gpt.svg';
import { NotesTopIcon } from 'components/VehicleCommon/Notes/NotesTopIcon';

const CheckboxToggleRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const ToggleLabel = styled.div`
  text-decoration: underline;
  color: #225a91;
  font-family: Roboto;
  font-size: 12px;
  font-weight: 700;
  line-height: 14px;
`;

const disabledCss = css`
  opacity: 0.7;
  pointer-events: none;
`;

const Container = styled.div<{ disabled: boolean }>`
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 15px;

  ${props => props.disabled && disabledCss}
`;

type Props = {
  sectionId: string;
  alwaysOpen?: boolean;
  autosave?: boolean;
  withAi?: boolean;
  disabled?: boolean;
};

export const CheckboxQuestions = ({
  sectionId,
  withAi = false,
  autosave = true,
  alwaysOpen = false,
  disabled = false
}: Props) => {
  const [checkboxesVisible, setCheckboxesVisible] = useState(alwaysOpen);
  const toggle = () => setCheckboxesVisible(curr => !curr);
  const { fields } = useFieldArray(`${sectionId}.checkboxes`, { subscription: { value: true } });
  const {
    input: { value: filters }
  } = useField('filters', { subscription: { value: true } });

  const filterSelected = filters && filters.show_comments;

  const isCheckboxVisible = (i: number) => {
    if (!filterSelected) return true;
    return !!fields.value?.[i]?.notes;
  };

  return (
    <>
      {!alwaysOpen && (
        <CheckboxToggleRow onClick={toggle}>
          <ToggleLabel>{checkboxesVisible ? 'Hide Checkboxes' : 'Unhide Checkboxes'}</ToggleLabel>
          <Icon style={{ display: 'flex' }}>{checkboxesVisible ? <DoubleCaretUp /> : <DoubleCaretDown />}</Icon>
        </CheckboxToggleRow>
      )}
      {checkboxesVisible && (
        <Container disabled={disabled}>
          {fields.map((name, i) => {
            if (!isCheckboxVisible(i)) return null;
            return (
              <CheckboxField
                withAi={withAi}
                index={i}
                autosave={autosave}
                isLast={filterSelected ? false : i === fields.length! - 1}
                sectionId={sectionId}
                name={name}
              />
            );
          })}
        </Container>
      )}
    </>
  );
};

type CheckboxProps = {
  sectionId: string;
  name: string;
  isLast: boolean;
  withAi?: boolean;
  autosave?: boolean;
  index?: number;
};

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MainFieldContent = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const OgTextLabel = styled.div`
  border-radius: 8px;
  color: #000000;
  padding: 4px 4px 4px 5px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  background: #ccccccb2;
  margin-left: auto;
  min-width: fit-content;
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 16.41px;
  color: #666666;
`;

const AddButtonWrapper = styled.div`
  margin-left: auto;
  margin-top: 8px;
`;

const AiStatusWrapper = styled.div`
  width: 11px;
  height: 11px;
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  display: flex;
  top: 4px;
  right: 4px;
  position: absolute;

  svg {
    width: 11px;
    height: 11px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  width: 32px;
  height: 32px;
  position: relative;
`;

export const CheckboxField = ({ sectionId, name, isLast, autosave, index, withAi = false }: CheckboxProps) => {
  const { fields } = useFieldArray(`${sectionId}.checkboxes`);

  const { input: notesInput } = useField(`${name}.notes.message`);

  const notesRef = useRef<HTMLTextAreaElement>(null);

  const [notesOpened, setNotesOpened] = useState(false);
  const toggleNotes = () => {
    if (notesInput.value) return;
    setNotesOpened(curr => !curr);
  };

  useEffect(() => {
    if (notesOpened && notesRef.current) {
      notesRef.current.focus();
    }
  }, [notesOpened]);

  const notesVisible = !!notesInput.value || notesOpened;

  const {
    input: { value: isEditing }
  } = useField(`${name}.isEditing`);

  const {
    input: { value: checkboxId }
  } = useField(`${name}.id`);

  const {
    input: { value: notesStatus }
  } = useField(`${name}.notes.status`);

  const isAiMessage = notesStatus === 'ai_used';

  const showOgMessageLabel = notesVisible && notesStatus === 'ai_used' && withAi;

  const [updateCheckbox] = useUpdateCheckbox(checkboxId, sectionId, index || -1);

  if (isEditing) {
    return <EditingCheckboxField autosave={autosave} sectionId={sectionId} name={name} />;
  }

  return (
    <FieldWrapper>
      <MainFieldContent>
        <IconWrapper onClick={toggleNotes}>
          <NotesIcon isActive={notesOpened} />
          {notesInput.value && <NotesTopIcon iconType={isAiMessage ? 'ai' : 'checkmark'} />}
        </IconWrapper>

        <Field name={`${name}.selected`}>
          {props => (
            <Checkbox
              color="primary"
              style={{ width: '24px', height: '24px', padding: 0 }}
              checked={props.input.value}
              onChange={e => {
                if (autosave) {
                  updateCheckbox({
                    selected: e.target.checked
                  });
                }

                props.input.onChange(e.target.checked);
              }}
            />
          )}
        </Field>
        <Field name={`${name}.label`}>{props => <Label>{props.input.value}</Label>}</Field>
        {showOgMessageLabel && <OgTextLabel>{'Original text'}</OgTextLabel>}
        {isLast && (
          <AddButtonWrapper
            onClick={() =>
              fields.push({
                selected: true,
                label: '',
                notes: '',
                isEditing: true
              })
            }
          >
            <AddCheckboxIcon />
          </AddButtonWrapper>
        )}
      </MainFieldContent>
      {notesVisible && (
        <CheckboxNotes
          withAi={withAi}
          notesRef={notesRef}
          fieldName={name}
          sectionId={sectionId}
          index={index}
          autosave={!!autosave}
        />
      )}
    </FieldWrapper>
  );
};
